"use client";

import { useState, useEffect } from "react";
import { IoClose, IoDownload, IoEye, IoLink } from "react-icons/io5";
import {
  MdImage,
  MdVideoFile,
  MdAudioFile,
  MdInsertDriveFile,
} from "react-icons/md";
import FilePreviewModal from "./FilePreviewModal";
import { useSelector } from "react-redux";

const MediaFilesModal = ({ selectedConversation, onClose }) => {
  const senderId = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId
  );
  const selectedConvoType = selectedConversation?.participants
    ? "group"
    : "simple";
  const receiverId = selectedConversation?.value;
  const [mediaFiles, setMediaFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState("files");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_URL;

  useEffect(() => {
    const fetchMediaHistory = async () => {
      setLoading(true);
      setError(null);
      const apiUrl =
        selectedConvoType === "group"
          ? `${baseURL}/GroupChat/GetInternalGroupChatMediaHistory/${receiverId}`
          : `${baseURL}/Chats/GetInternalChatMediaHistory/${senderId}/${receiverId}`;
      try {
        const response = await fetch(
          `${apiUrl}?filterType=${activeTab === "files" ? "File" : "Link"}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch media history");
        }

        const data = await response.json();

        // Process the API response to separate files and links
        const files = [];
        const links = [];

        data.forEach((item) => {
          if (
            item.content &&
            (item.content.startsWith("http://") ||
              item.content.startsWith("https://"))
          ) {
            // This is a link
            links.push({
              id: item.id,
              url: item.content,
              title: item.fileName || "Shared Link",
              domain: new URL(item.content).hostname,
              uploadedAt: item.createdAt,
              messageId: item.id.toString(),
              senderName: item.senderName || "Unknown", // Add sender name
            });
          } else if (item.fileName && item.extension) {
            // This is a file
            const fileType = getFileTypeFromExtension(item.extension);
            files.push({
              id: item.id,
              name: item.fileName,
              type: fileType,
              extension: item.extension,
              uploadedAt: item.createdAt,
              messageId: item.id.toString(),
              senderName: item.senderName || "Unknown", // Add sender name
            });
          }
        });

        setMediaFiles(files);
        setLinks(links);
      } catch (err) {
        console.error("Error fetching media history:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (senderId && receiverId) {
      fetchMediaHistory();
    }
  }, [senderId, receiverId, activeTab]);

  const getFileTypeFromExtension = (extension) => {
    const imageExtensions = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".bmp",
      ".webp",
      ".jfif",
    ];
    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
    const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a"];

    if (imageExtensions.includes(extension.toLowerCase())) {
      return "image";
    } else if (videoExtensions.includes(extension.toLowerCase())) {
      return "video";
    } else if (audioExtensions.includes(extension.toLowerCase())) {
      return "audio";
    }
    return "document";
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "image":
        return <MdImage className="w-6 h-6 text-blue-500" />;
      case "video":
        return <MdVideoFile className="w-6 h-6 text-red-500" />;
      case "audio":
        return <MdAudioFile className="w-6 h-6 text-green-500" />;
      default:
        return <MdInsertDriveFile className="w-6 h-6 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownload = async (file) => {
    try {
      const url =
        selectedConvoType === "group"
          ? `${baseURL}/GroupChat/Download-File/${file.messageId}`
          : `${baseURL}/Chats/download/${file.messageId}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        file.name || `file_${file.messageId}${file.extension || ""}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download file");
    }
  };

  const handlePreview = (file) => {
    setSelectedFile(file);
  };

  const renderFilesTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6 overflow-y-auto">
      {mediaFiles.map((file, index) => (
        <div
          key={index}
          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-start gap-3">
            {getFileIcon(file.type)}
            <div className="flex-1 min-w-0">
              <h3
                className="font-medium text-gray-800 truncate"
                title={file.name}
              >
                {file.name}
              </h3>
              <p className="text-sm text-gray-500">{file.extension}</p>
              <div className="flex  flex-col justify-between items-center mt-1">
                <p className="text-xs text-gray-400">
                  Sent by: {file.senderName}
                </p>
                <p className="text-xs text-gray-400">
                  {formatDate(file.uploadedAt)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={() => handlePreview(file)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
            >
              <IoEye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={() => handleDownload(file)}
              className="flex items-center justify-center px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              <IoDownload className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderLinksTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 overflow-y-auto">
      {links.map((link, index) => (
        <div
          key={index}
          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-start gap-3">
            <IoLink className="w-6 h-6 text-purple-500" />
            <div className="flex-1 min-w-0">
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-800 hover:underline block truncate"
                title={link.url}
              >
                {link.title}
              </a>
              <div className="flex flex-col items-center justify-between mt-2">
                <span className="text-xs text-gray-400">{link.domain}</span>
                <span className="text-xs text-gray-400">
                  Sent by: {link.senderName}
                </span>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {formatDate(link.uploadedAt)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {activeTab === "files" ? "Shared Files" : "Shared Links"}
            </h2>
            <p className="text-sm text-gray-600">
              {selectedConversation?.label} •{" "}
              {activeTab === "files" ? mediaFiles.length : links.length}{" "}
              {activeTab === "files" ? "files" : "links"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 ${
              activeTab === "files"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("files")}
          >
            <MdInsertDriveFile className="w-5 h-5" />
            Files
          </button>
          <button
            className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 ${
              activeTab === "links"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("links")}
          >
            <IoLink className="w-5 h-5" />
            Links
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto  max-h-[60vh] min-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center min-h-[60vh] ">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center  text-red-500 min-h-[60vh]">
              <IoClose className="w-16 h-16 mb-4" />
              <p>Error loading media</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : activeTab === "files" ? (
            mediaFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center  text-gray-500 min-h-[60vh]">
                <MdInsertDriveFile className="w-16 h-16 mb-4" />
                <p>No files shared yet</p>
              </div>
            ) : (
              renderFilesTab()
            )
          ) : links.length === 0 ? (
            <div className="flex flex-col items-center justify-center  text-gray-500 min-h-[60vh]">
              <IoLink className="w-16 h-16 mb-4" />
              <p>No links shared yet</p>
            </div>
          ) : (
            renderLinksTab()
          )}
        </div>

        {/* File Preview Modal */}
        {selectedFile && (
          <FilePreviewModal
            file={selectedFile}
            onClose={() => setSelectedFile(null)}
            selectedConversationType={selectedConvoType}
          />
        )}
      </div>
    </div>
  );
};

export default MediaFilesModal;
